import React from 'react';
import {
  AppBar,
  Toolbar,
  Avatar,
  Typography,
  useScrollTrigger,
  Slide,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { Box } from '@mui/system';
import logo from '../static/15th-logo.png';
import UpdateIcon from '@mui/icons-material/Update';
import MenuIcon from '@mui/icons-material/Menu';
import { Link, NavLink } from 'react-router-dom';

interface Props {
  /**
   * Injected by the documentation to work in an iframe.
   * You won't need it on your project.
   */
  window?: () => Window;
  children: React.ReactElement;
}

function HideOnScroll(props: Props) {
  const { children, window } = props;
  // Note that you normally won't need to set the window ref as useScrollTrigger
  // will default to window.
  // This is only being set here because the demo is in an iframe.
  const trigger = useScrollTrigger({
    target: window ? window() : undefined,
  });

  return (
    <Slide appear={false} direction='down' in={!trigger}>
      {children}
    </Slide>
  );
}

const Header = ({ updateDate }: { updateDate: string }) => {
  return (
    <header>
      <HideOnScroll>
        <AppBar>
          <Toolbar>
            <Box sx={{ paddingLeft: '24px' }}>
              <Avatar
                sx={{ width: 75, height: 75 }}
                variant='rounded'
                src={logo}
                className='App-logo'
                alt='logo'
              />
            </Box>
            <Box
              sx={{
                marginRight: 'auto',
                display: 'flex',
                flexDirection: 'column',
                padding: '35px',
              }}
              component='div'
            >
              <Typography variant='h6'>
                Department of Air Force <br /> Water Updates
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <Box sx={{ display: 'flex', gap: '4px' }}>
                <UpdateIcon />
                <Typography variant='body2'>LAST UPDATED:</Typography>
              </Box>
              <Box sx={{ paddingRight: '24px' }}>
                <Typography variant='body2'>{updateDate}</Typography>
              </Box>
            </Box>
          </Toolbar>
        </AppBar>
      </HideOnScroll>
    </header>
  );
};

const MobileHeader = ({ updateDate }: { updateDate: string }) => {
  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position='static'>
        <Box sx={{ position: 'absolute', top: 10, left: 10 }}>
          <NavigationMenu />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '10px',
            padding: '10px 0',
          }}
        >
          <Avatar
            sx={{ width: 54, height: 56 }}
            variant='rounded'
            src={logo}
            className='App-logo'
            alt='logo'
          />

          <Typography variant='body1'>
            Department of Air Force Water Updates
          </Typography>

          <Box
            sx={{
              display: 'flex',
              gap: '5px',
              alignContent: 'center',
              justifyItems: 'center',
            }}
          >
            <UpdateIcon sx={{ height: '12px', width: '12px' }} />
            <Typography sx={{ fontSize: '9px' }}>LAST UPDATED:</Typography>
            <Typography sx={{ fontSize: '9px' }}>{updateDate}</Typography>
          </Box>
        </Box>
      </AppBar>
    </Box>
  );
};

export default function NavigationMenu() {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'start', textAlign: 'start' }}>
        <Tooltip title='Account settings'>
          <IconButton
            onClick={handleClick}
            size='small'
            aria-controls={open ? 'account-menu' : undefined}
            aria-haspopup='true'
            aria-expanded={open ? 'true' : undefined}
          >
            <MenuIcon sx={{ width: 32, height: 32, color: 'white' }} />
          </IconButton>
        </Tooltip>
      </Box>
      <Menu
        anchorEl={anchorEl}
        id='account-menu'
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      >
        <MenuItem>
          <NavLink style={{ textDecoration: 'none', color: 'black' }} to='/'>
            Home
          </NavLink>
        </MenuItem>
        <MenuItem>
          <Link style={{ textDecoration: 'none', color: 'black' }} to='/guide'>
            Guide
          </Link>
        </MenuItem>
        <MenuItem>
          <Link
            style={{ textDecoration: 'none', color: 'black' }}
            to='/flowchart'
          >
            Flowchart
          </Link>
        </MenuItem>
        <MenuItem>
          <Link
            style={{ textDecoration: 'none', color: 'black' }}
            to='/resources'
          >
            Resources
          </Link>
        </MenuItem>
        <MenuItem>
          <Link style={{ textDecoration: 'none', color: 'black' }} to='/faq'>
            FAQ
          </Link>
        </MenuItem>
        <MenuItem>
          <Link
            style={{ textDecoration: 'none', color: 'black' }}
            to='/contact'
          >
            Contact
          </Link>
        </MenuItem>
      </Menu>
    </>
  );
}

export { Header, MobileHeader };
